import {
  ChecklistIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  FileDiffIcon,
  GitCommitIcon,
  ShieldIcon,
  TasklistIcon,
} from '@primer/octicons-react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import {pullRequestsChangesRoute} from '../../routes/files-changed-routes'
import {pullRequestsChecksRoute} from '../../routes/checks-route'
import {pullRequestsCommitsRoute} from '../../routes/commits-route'
import {pullRequestsConversationsRoute} from '../../routes/conversations-route'
import {pullRequestsFindingsRoute} from '../../routes/findings-route'
import {pullRequestsOverviewIndexRoute, pullRequestsOverviewRoute} from '../../routes/overview-route'
import {useIsFeatureStateEnabled} from '../../hooks/use-feature-state'
import type {NavigationCounterPageData} from '../../page-data/payloads/tab-counts'
import type {HeaderRepository} from '../../page-data/payloads/header'
import type {PullRequest} from '../../types'
import type {NavigationUrls} from '../../types/navigation-urls-types'
import type {HeaderTab} from './types'

export type UseHeaderTabsInput = {
  pullRequest: PullRequest
  repository: HeaderRepository
  urls: NavigationUrls
  /**
   * Async-loaded counts for the tab badges. When `undefined`, async counts render in a
   * "loading" placeholder state (transparent text inside the counter chip).
   */
  labelCounts: NavigationCounterPageData | undefined
}

/**
 * Returns the ordered list of PR header tabs (Overview, Conversation, Commits, Checks,
 * Findings, Files changed, Walkthrough) filtered by the feature flags / feature states that
 * gate each one. Overview replaces Conversation as the index route
 * Both the underline-nav tabs and the sticky-mini-header tab
 * switcher render from this single source so the menu stays in sync with the nav.
 */
export function useHeaderTabs({pullRequest, repository, urls, labelCounts}: UseHeaderTabsInput): HeaderTab[] {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isChangesEnabled = useFeatureFlag('react_data_router_pull_request_files')
  const findingsEnabled = useIsFeatureStateEnabled('findings')
  const walkthroughEnabled = isFeatureEnabled('hypersight')
  const overviewEnabled = useIsFeatureStateEnabled('overview')

  const routeParams = {
    owner: repository.ownerLogin,
    repo: repository.name,
    pr_number: pullRequest.number.toString(),
  }

  const countLoading = labelCounts === undefined

  const tabs: HeaderTab[] = []

  const overviewTab: HeaderTab = {
    kind: 'router',
    key: 'overview',
    label: 'Overview',
    icon: TasklistIcon,
    to: overviewEnabled
      ? pullRequestsOverviewIndexRoute.generatePath(routeParams)
      : pullRequestsOverviewRoute.generatePath(routeParams),
    preload: 'intent',
  }

  if (overviewEnabled) {
    tabs.push(overviewTab)
  } else {
    tabs.push({
      kind: 'router',
      key: 'conversation',
      label: 'Conversation',
      icon: CommentDiscussionIcon,
      to: pullRequestsConversationsRoute.generatePath(routeParams),
      count: labelCounts?.conversationCount,
      countLoading,
      preload: 'intent',
    })

    if (overviewEnabled) {
      tabs.push(overviewTab)
    }
  }

  tabs.push({
    kind: 'router',
    key: 'commits',
    label: 'Commits',
    icon: GitCommitIcon,
    headerNavId: 'prs-commits-anchor-tab',
    to: pullRequestsCommitsRoute.generatePath(routeParams),
    // commitsCount is provided synchronously from the PR payload, not from the async counts query.
    count: typeof pullRequest.commitsCount === 'number' ? pullRequest.commitsCount : undefined,
  })

  tabs.push({
    kind: 'router',
    key: 'checks',
    label: 'Checks',
    icon: ChecklistIcon,
    to: pullRequestsChecksRoute.generatePath(routeParams),
    count: labelCounts?.checksCount,
    countLoading,
    preload: 'intent',
  })

  if (findingsEnabled) {
    tabs.push({
      kind: 'router',
      key: 'findings',
      label: 'Findings',
      icon: ShieldIcon,
      to: pullRequestsFindingsRoute.generatePath(routeParams),
      count: findingsCountOf(labelCounts),
      countLoading,
      preload: 'intent',
    })
  }

  tabs.push(
    isChangesEnabled
      ? {
          kind: 'router',
          key: 'files_changed',
          label: 'Files changed',
          icon: FileDiffIcon,
          headerNavId: 'prs-files-anchor-tab',
          to: pullRequestsChangesRoute.generatePath(routeParams),
          preload: 'intent',
          preloadDelay: 100,
          count: labelCounts?.filesChangedCount,
          countLimitExceeded: labelCounts?.filesChangedCountLimitExceeded,
          countLoading,
        }
      : {
          kind: 'href',
          key: 'files_changed',
          label: 'Files changed',
          icon: FileDiffIcon,
          headerNavId: 'prs-files-anchor-tab',
          href: urls.files,
          count: labelCounts?.filesChangedCount,
          countLimitExceeded: labelCounts?.filesChangedCountLimitExceeded,
          countLoading,
        },
  )

  if (walkthroughEnabled) {
    tabs.push({
      kind: 'href',
      key: 'walkthrough',
      label: 'Walkthrough',
      icon: CopilotIcon,
      href: urls.walkthrough,
      prefetch: 'moderate',
    })
  }

  return tabs
}

/**
 * Convenience hook that fetches the async tab counts via `useRouteQuery` and returns the
 * tabs already wired up. Use when you don't already have `labelCounts` in scope (e.g. in
 * the sticky mini-header switcher).
 */
export function useHeaderTabsWithCounts(input: Omit<UseHeaderTabsInput, 'labelCounts'>): HeaderTab[] {
  const {data: labelCounts} = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')
  return useHeaderTabs({...input, labelCounts})
}

/** Findings only shows a counter when the count is positive. */
function findingsCountOf(labelCounts: NavigationCounterPageData | undefined): number | undefined {
  const value = labelCounts?.findingsCount
  return typeof value === 'number' && value > 0 ? value : undefined
}
